/**
 * One choice among a few, drawn as pills — the designer's audit 3.4 and D4 (`docs/adr/9030`).
 *
 * The decision panels drew their choices (the engine reason group, the disposition) as native
 * radios styled with `accent-ink-primary`, which in the dark theme is a solid white disc, and the
 * mockup draws pills. This is one component for every such choice, so the next panel does not
 * restyle a radio of its own.
 *
 * **Still a radio group underneath.** Each pill is a visually hidden `<input type="radio">` with
 * its `<label>`: the browser supplies the arrow keys, the group name, the checked state and the
 * accessible name, and a test that finds a choice by `getByRole('radio', { name })` keeps working.
 * Nothing is pre-selected — a pre-selected first option is a choice made by whatever sorted first.
 *
 * **A required choice refuses in red, with a shake.** `RequiredChoice` wraps any control (these
 * pills, or a `Select`). While the choice is empty it states what is missing in the ordinary
 * caption; when the action is pressed without it (`refusals` counts the presses) the message turns
 * `status-critical-strong` with an icon — never colour alone — and the control shakes once per
 * press. Under reduced motion the shake finishes in 0.01ms (ADR 9029), so only the colour remains.
 */

import { CircleAlert } from 'lucide-react';
import { useEffect, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { MOTION } from '../primitives/motion';

export interface ChipOption {
  readonly value: string;
  /** Already resolved: the platform's own prose, or a catalogue message the caller rendered. */
  readonly label: string;
  /**
   * Listed but not choosable (a native `disabled` radio): an option the vocabulary publishes and
   * this decision cannot take — the panel says why beside the group.
   */
  readonly disabled?: boolean;
}

export interface RequiredChoiceProps {
  /** What is missing, said as the reason the action cannot fire. */
  readonly messageKey: string;
  /** Whether the choice is empty. */
  readonly missing: boolean;
  /** How many times the action was pressed while the choice was empty; each press shakes once. */
  readonly refusals: number;
  readonly children: React.ReactNode;
}

export function RequiredChoice({ messageKey, missing, refusals, children }: RequiredChoiceProps): React.JSX.Element {
  const { t } = useTranslation();
  const [shaking, setShaking] = useState(false);
  useEffect(() => {
    if (refusals > 0) setShaking(true);
  }, [refusals]);
  const refused = missing && refusals > 0;
  return (
    <div className="flex flex-col gap-2" data-required-choice={refused ? 'refused' : missing ? 'missing' : 'chosen'}>
      <div
        className={shaking ? MOTION.shake : ''}
        onAnimationEnd={() => setShaking(false)}
      >
        {children}
      </div>
      {missing && (
        refused ? (
          <p role="alert" className="text-caption text-status-critical-strong flex items-center gap-1 font-semibold">
            <CircleAlert aria-hidden="true" className="size-[14px] shrink-0" />
            {t(messageKey)}
          </p>
        ) : (
          <p className="text-caption text-ink-secondary max-w-prose">{t(messageKey)}</p>
        )
      )}
    </div>
  );
}

export interface ChipGroupProps {
  readonly legendKey: string;
  readonly options: readonly ChipOption[];
  /** `null` until chosen. There is no initial choice. */
  readonly value: string | null;
  readonly onChange: (value: string) => void;
  /** Anything the group should say under its pills (a note on what the choice records). */
  readonly children?: React.ReactNode;
}

export function ChipGroup({ legendKey, options, value, onChange, children }: ChipGroupProps): React.JSX.Element {
  const { t } = useTranslation();
  const name = useId();
  return (
    <fieldset className="flex min-w-0 flex-col gap-2">
      <legend className="text-label text-ink-secondary">{t(legendKey)}</legend>
      <div className="flex flex-wrap gap-2">
        {options.map((option) => {
          const id = `${name}-${option.value}`;
          const checked = value === option.value;
          return (
            <span key={option.value} className="inline-flex">
              <input
                type="radio"
                id={id}
                name={name}
                value={option.value}
                checked={checked}
                disabled={option.disabled === true}
                onChange={() => onChange(option.value)}
                className="peer sr-only"
              />
              <label
                htmlFor={id}
                className={[
                  'text-label rounded-button inline-flex min-h-8 items-center border px-3 py-1',
                  'transition-colors peer-focus-visible:outline peer-focus-visible:outline-2',
                  'peer-focus-visible:outline-offset-2 peer-focus-visible:outline-focus-ring',
                  checked
                    ? 'bg-ink-primary text-surface-page border-ink-primary cursor-pointer'
                    : option.disabled === true
                      ? 'text-ink-muted border-hairline cursor-not-allowed'
                      : 'text-ink-primary hover:bg-surface-sunken border-hairline cursor-pointer',
                ].join(' ')}
              >
                {option.label}
              </label>
            </span>
          );
        })}
      </div>
      {children}
    </fieldset>
  );
}
